import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { SettingsGender, Languages } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { GENDERS } from '../../../../core/constants'
import {
  deleteSettingsGender,
  getSettingsGender,
  updateSettingsGender,
} from '../../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Input } from '@/components/ui'

export interface IGendersFormValues {
  genders?: {
    id?: string
    name: string
    translations: {
      id?: string
      name: string
      locale: string
    }[]
  }[]
}

interface GenderProps {
  allLanguages: Languages[]
  hasEditPermission: boolean
}

export const Genders: React.FC<GenderProps> = (props: GenderProps) => {
  const { allLanguages, hasEditPermission } = props
  const { editionId, festivalName } = useParams()
  const [
    showDeleteConfirmationModalGenders,
    setDeleteConfirmationModalGenders,
  ] = useState<boolean>(false)
  const [selectedGenderIndex, setSelectedGenderIndex] = useState<
    number | undefined
  >()
  const [genders, setGenders] = useState<SettingsGender[]>()

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const defaultLanguageContent = map(allLanguages, (lang) => {
    return {
      locale: lang.locale,
      name: '',
    }
  })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsGender()
        if (resp.status === 200) {
          setGenders(resp.data)
          const temp = map(resp.data, (gender) => {
            const transTemp = defaultLanguageContent
            map(gender.translations, trans => {
              const index = findIndex(allLanguages, l => l.locale === trans.locale)
              if (index !== -1) {
                transTemp[index].locale = trans.locale
                transTemp[index].name = trans.name
              }
            })
            return ({
              id: gender.id,
              name: gender.name,
              translations: transTemp,
            })
          })
          setTimeout(() => {
            setValue('genders', temp)
          }, 200)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName, allLanguages])

  const schema = yup.object().shape({
    genders: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().optional(),
          name: yup.string().required('Field is required'),
          translations: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().optional(),
                locale: yup.string().required('Field is required'),
                name: yup.string().required('Field is required'),
              }),
            )
            .required(),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
    control,
  } = useForm<IGendersFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IGendersFormValues>(schema),
    defaultValues: {},
  })

  const {
    fields: gendersFields,
    append: appendGenders,
    remove: removeGenders,
  } = useFieldArray({
    control: control,
    name: 'genders',
  })

  const gendersWatch = watch('genders')

  const submit = async (values: IGendersFormValues) => {
    try {
      if (values?.genders) {
        const { status } = await updateSettingsGender(values.genders)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    const temp = map(genders, (gender) => {
      const transTemp = defaultLanguageContent
      map(gender.translations, (trans) => {
        const index = findIndex(allLanguages, (l) => l.locale === trans.locale)
        if (index !== -1) {
          transTemp[index].locale = trans.locale
          transTemp[index].name = trans.name
        }
      })
      return {
        id: gender.id,
        name: gender.name,
        translations: transTemp,
      }
    })
    setTimeout(() => {
      setValue('genders', temp)
    }, 200)
  }

  const deleteGender = async () => {
    try {
      if (selectedGenderIndex) {
        removeGenders(selectedGenderIndex)
        if (gendersWatch?.[selectedGenderIndex]?.id) {
          const resp = await deleteSettingsGender(
            gendersWatch?.[selectedGenderIndex]?.id || '',
          )
          if (resp.status === 204) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Translations Successful')
          }
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  return (
    <div>
      <div>
        <h4 className="text-title-s text-accent-1 mb-4">Genders</h4>
      </div>
      <Card className="border border-monochrome-120">
        <div className="p-3">
          <form id="settings-gender-form" onSubmit={handleSubmit(submit)}>
            {map(gendersFields, (field, index) => (
              <FormGroup key={field.id}>
                <Card className="w-full p-4 rounded-2xl">
                  <div className="flex flex-col gap-3">
                    {map(allLanguages, (language, i) => {
                      return (
                        <div
                          className="flex flex-col p-3 gap-2 border border-monochrome-120 rounded-2xl"
                          key={language.id}
                        >
                          <FormGroup
                            label="Language:"
                            direction="row"
                            wrapperClassName="mb-0"
                          >
                            <span className="text-sm font-semibold text-accent-1">
                              {language.name}
                            </span>
                          </FormGroup>
                          <FormGroup
                            label="Translation"
                            wrapperClassName="mb-0"
                          >
                            <Input
                              isInvalid={
                                !!errors?.genders?.[index]?.translations?.[i]
                                  ?.name
                              }
                              {...register(
                                `genders.${index}.translations.${i}.name`,
                              )}
                              onChange={(e) => {
                                setValue(
                                  `genders.${index}.translations.${i}.locale`,
                                  language.locale,
                                )
                              }}
                              type="text"
                              disabled={!hasEditPermission}
                            />
                          </FormGroup>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="destructive"
                      type="button"
                      disabled={!hasEditPermission}
                      onClick={() => {
                        setDeleteConfirmationModalGenders(true)
                        setSelectedGenderIndex(index)
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card>
              </FormGroup>
            ))}
            <Button
              onClick={() => {
                if (gendersWatch?.length) {
                  const rest = GENDERS.filter((one) => {
                    let isSelected = false
                    gendersWatch.forEach((gender) => {
                      if (gender.name === one) {
                        isSelected = true
                      }
                    })
                    if (!isSelected) {
                      return one
                    }
                  })
                  appendGenders({
                    name: rest[0],
                    translations: [],
                  })
                } else {
                  appendGenders({
                    name: GENDERS[0],
                    translations: [],
                  })
                }
              }}
              disabled={!hasEditPermission}
              type="button"
              variant="secondary"
              className="w-full mt-2"
            >
              Add New Gender
            </Button>
          </form>
        </div>
        <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-gender-form"
            type="submit"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModalGenders}
        setDeleteConfirmationModal={setDeleteConfirmationModalGenders}
        deleteTitle={'Genders'}
        deleteFunc={deleteGender}
        onClose={() => setSelectedGenderIndex(undefined)}
      />
    </div>
  )
}
